<template>
  <div class="bg-white border-b border-gray-100 shadow-sm py-8">
    <div class="max-w-6xl mx-auto px-6">
      <!-- Étapes du stepper - visible seulement sur desktop -->
      <div class="hidden md:flex items-center justify-center">
        <div 
          v-for="(step, index) in steps" 
          :key="index"
          class="flex items-center"
        >
          <div class="flex flex-col items-center gap-3">
            <!-- Cercle de l'étape -->
            <div 
              class="flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 ease-in-out shadow-md"
              :class="getStepClasses(index)"
            >
              <span 
                class="text-sm font-bold"
                :class="index <= currentStep ? 'text-white' : 'text-gray-600'"
              >
                {{ index + 1 }}
              </span>
            </div>

            <!-- Titre de l'étape -->
            <div class="relative flex flex-col items-center min-w-[120px]">
              <span
                class="text-sm font-semibold text-center transition-all duration-300 leading-tight"
                :class="getStepTitleClasses(index)"
              >
                {{ step.title }}
              </span>
              
              <!-- Indicateur d'étape actuelle -->
              <div 
                v-if="index === currentStep"
                class="w-2 h-2 bg-blue-500 rounded-full mt-2 animate-pulse"
              ></div>
            </div>
          </div>
          
          <!-- Ligne de connexion (sauf pour la dernière étape) -->
          <div 
            v-if="index < steps.length - 1"
            class="flex items-center min-w-[10rem] -mx-8 mb-4"
          >
            <div 
              class="w-full h-1 transition-all duration-300 ease-in-out rounded-full"
              :class="getStepLineClasses(index)"
            ></div>
          </div>
        </div>
      </div>
      
      <!-- Bulle de progression mobile -->
      <div class="md:hidden flex justify-center mt-6">
        <div class="relative">
          <!-- Cercle de progression -->
          <div class="w-20 h-20 rounded-full bg-white relative flex items-center justify-center">
            <!-- Arc de progression -->
            <svg class="w-20 h-20 absolute inset-0 transform -rotate-90" viewBox="0 0 80 80">
              <circle
                cx="40"
                cy="40"
                r="32"
                fill="none"
                stroke="#10b981"
                stroke-width="6"
                stroke-linecap="round"
                :stroke-dasharray="`${getProgressArc()} 200`"
                class="transition-all duration-500 ease-in-out"
              />
            </svg>
            <!-- Texte de progression -->
            <div class="text-center z-10">
              <div class="text-lg font-bold text-gray-800">{{ currentStep + 1 }}</div>
              <div class="text-xs text-gray-500">of {{ steps.length }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { StepperHeaderProps } from '@/types/sellerRegister';

const props = defineProps<StepperHeaderProps>();

const getStepClasses = (index: number) => {
  if (index <= props.currentStep) {
    return 'bg-blue-500 border-blue-500 text-white scale-105 shadow-lg shadow-blue-500/30';
  } else {
    return 'bg-white border-gray-300 text-gray-600';
  }
};

const getStepTitleClasses = (index: number) => {
  if (index === props.currentStep) {
    return 'text-blue-600 scale-105';
  } else if (index < props.currentStep || props.steps[index].completed) {
    return 'text-gray-900';
  } else {
    return 'text-gray-400';
  }
};

const getStepLineClasses = (index: number) => {
  if (index < props.currentStep) {
    return 'bg-blue-500';
  } else {
    return 'bg-gray-200';
  }
};

const getProgressArc = () => {
  // Calculer le pourcentage de progression basé sur l'étape actuelle
  const progressPercentage = (props.currentStep / props.steps.length) * 100;
  
  // Calculer la circonférence de l'arc (2 * π * r)
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  
  // Calculer la longueur de l'arc basée sur le pourcentage
  const arcLength = (progressPercentage / 100) * circumference;
  
  return arcLength;
};
</script>

<style scoped>
@media (max-width: 768px) {
  .max-w-6xl {
    max-width: 100%;
  }
  
  .px-6 {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  .gap-8 {
    gap: 1rem;
  }
  
  .w-12.h-12 {
    width: 2.5rem;
    height: 2.5rem;
  }
  
  .text-sm {
    font-size: 0.75rem;
  }
  
  .min-w-\[120px\] {
    min-width: 80px;
  }
  
  .min-w-\[4rem\] {
    min-width: 2.5rem;
  }
  
  .mx-2 {
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }
}

@media (max-width: 640px) {
  .flex.items-center.justify-center {
    flex-direction: column;
    gap: 1.5rem;
  }
  
  .flex.items-center {
    flex-direction: column;
  }
  
  .min-w-\[4rem\] {
    width: 3px;
    height: 2rem;
    min-width: auto;
  }
  
  .h-0\.5 {
    width: 3px;
    height: 100%;
  }
}
</style>
